import logger from '../utils/logger.js';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Person as PersonIcon,
  LocalHospital as MedicalIcon,
  Info as InfoIcon,
  Psychology as PsychologyIcon,
  ArrowBack as ArrowBackIcon,
  AccountCircle as GuestIcon,
  MedicalServices as SuperDoctorIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PageLayout from '../components/PageLayout';
import PageHeader from '../components/PageHeader';
import PatientProfileAvatar from '../components/PatientProfileAvatar';
import SessionModeToggle from '../components/SessionModeToggle';
import axios from 'axios';
import { DEFAULT_SESSION_MODE } from '../utils/sessionMode';
import { sessionModeOptionsForUser } from '../utils/moduleAccess';
import { SIMULATION_SETUP_PATH } from '../constants/clientRoutes';

const PersonaList = () => {
  const navigate = useNavigate();
  const { user, isGuest, isAuthenticated } = useAuth();
  const [personas, setPersonas] = useState([]);
  const [superDoctors, setSuperDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [selectedSuperDoctor, setSelectedSuperDoctor] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [startingSimulation, setStartingSimulation] = useState(false);
  const [selectedMode, setSelectedMode] = useState(DEFAULT_SESSION_MODE);
  const sessionModeOptions = React.useMemo(
    () => sessionModeOptionsForUser(user),
    [user]
  );

  React.useEffect(() => {
    if (selectedMode === 'nurse' && !sessionModeOptions.some((option) => option.value === 'nurse')) {
      setSelectedMode(DEFAULT_SESSION_MODE);
    }
  }, [selectedMode, sessionModeOptions]);

  const fetchPersonasAndSuperDoctors = useCallback(async () => {
    try {
      logger.info('🎭 Fetching personas and Super Doctors...');
      logger.info('🔐 Auth state:', { user: user?.email, isGuest, isAuthenticated });
      
      const headers = {};
      const token = localStorage.getItem('token');
      
      if (token && user && !isGuest) {
        headers.Authorization = `Bearer ${token}`;
        logger.info('🔑 Making authenticated request with token:', token.substring(0, 20) + '...');
      } else if (isGuest) {
        logger.info('� Making request as guest user (no auth required)');
      } else {
        logger.info('⚠️ Making unauthenticated request - user not logged in');
      }

      logger.info('📡 Making request to /api/simulation/available-personas with headers:', headers);
      
      const response = await axios.get('/api/simulation/available-personas', { headers });
      logger.info('✅ Available personas response:', {
        status: response.status,
        patients: response.data.patients?.length,
        superDoctors: response.data.superDoctors?.length
      });
      
      setPersonas(response.data.patients || []);
      setSuperDoctors(response.data.superDoctors || []);
      setError('');
    } catch (error) {
      logger.error('❌ Failed to fetch personas and Super Doctors:', error);
      logger.error('❌ Error response:', error.response?.data);
      logger.error('❌ Error status:', error.response?.status);
      
      if (error.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else {
        setError('Failed to load patient personas. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [user, isGuest, isAuthenticated]);

  useEffect(() => {
    logger.info('🎭 PersonaList mounted with auth state:', { 
      hasUser: !!user, 
      isGuest, 
      isAuthenticated 
    });
    fetchPersonasAndSuperDoctors();
  }, [user, isGuest, fetchPersonasAndSuperDoctors, isAuthenticated]);

  const handleSelectPersona = (persona) => {
    logger.info('🎯 Selected persona:', persona);
    setSelectedPersona(persona);
    setShowConfirmDialog(true);
    setError('');
  };

  const handleStartSimulation = async () => {
    if (!selectedPersona) {
      logger.error('❌ No persona selected');
      return;
    }

    logger.info('🚀 Starting simulation with persona:', {
      id: selectedPersona.id,
      name: selectedPersona.name
    });
    logger.info('🔐 Current auth state:', { 
      user: user?.email, 
      isGuest, 
      isAuthenticated 
    });
    
    setStartingSimulation(true);
    setError('');
    
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = localStorage.getItem('token');
      
      if (token) {
        headers.Authorization = `Bearer ${token}`;
        logger.info('🔑 Using token for simulation start:', token.substring(0, 20) + '...');
      } else {
        logger.info('🔑 No token - attempting as guest');
      }

      const requestData = {
        personaId: selectedPersona.id,
        mode: selectedMode,
        superDoctorId: selectedMode === 'nurse' ? null : (selectedSuperDoctor?.id || null)
      };

      logger.info('📡 POST /api/simulation/start');
      logger.info('📤 Request headers:', headers);
      logger.info('📤 Request data:', requestData);

      const response = await axios.post('/api/simulation/start', requestData, { headers });
      
      logger.info('✅ Simulation start response:', response.data);
      logger.info('✅ Response status:', response.status);
      logger.info('✅ Session ID:', response.data.sessionId);
      
      if (response.data.sessionId) {
        const targetPath = `/simulation/${response.data.sessionId}`;
        logger.info('🧭 Navigating to:', targetPath);
        
        setShowConfirmDialog(false);
        
        // Small delay to ensure dialog closes before navigation
        setTimeout(() => {
          logger.info('🧭 Executing navigation...');
          navigate(targetPath);
        }, 100);
      } else {
        throw new Error('No session ID returned from server');
      }
    } catch (error) {
      logger.error('❌ Failed to start simulation:', error);
      logger.error('❌ Error response data:', error.response?.data);
      logger.error('❌ Error response status:', error.response?.status);
      logger.error('❌ Error response headers:', error.response?.headers);
      logger.error('❌ Full error object:', error);
      
      let errorMessage = 'Failed to start simulation. Please try again.';
      
      if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to start simulations.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Selected persona not found.';
      } else if (error.response?.data?.error) {
        errorMessage = `Error: ${error.response.data.error}`;
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      logger.error('❌ Setting error message:', errorMessage);
      setError(errorMessage);
      setShowConfirmDialog(false);
    } finally {
      setStartingSimulation(false);
    }
  };

  const handleCloseDialog = () => {
    if (!startingSimulation) {
      setShowConfirmDialog(false);
      setSelectedPersona(null);
      setSelectedSuperDoctor(null);
      setError('');
    }
  };

  const getPersonalityTraits = (personalityTraits) => {
    let traits = [];
    
    if (typeof personalityTraits === 'string') {
      // Remove the slice(0, 3) to show all traits
      traits = personalityTraits.split(',');
    } else if (typeof personalityTraits === 'object' && personalityTraits !== null) {
      // Ensure we get all 5 Big Five traits in a specific order
      const orderedTraits = ['openness', 'conscientiousness', 'extraversion', 'agreeableness', 'neuroticism'];
      
      traits = orderedTraits.map(trait => {
        const value = personalityTraits[trait];
        if (value !== undefined) {
          // Use shorter labels for better display
          const shortLabels = {
            openness: 'Openness',
            conscientiousness: 'Conscientiousness',
            extraversion: 'Extraversion', 
            agreeableness: 'Agreeableness',
            neuroticism: 'Neuroticism'
          };
          return `${shortLabels[trait] || trait}: ${value}/5`;
        }
        return null;
      }).filter(Boolean);
    }
    
    return traits;
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'easy': return 'success';
      case 'medium': return 'warning';
      case 'hard': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={48} />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageHeader
        title="Patient Personas"
        subtitle="Select a patient to start your medical interview simulation"
        breadcrumbs={[
          { label: 'Patient Selection', path: null }
        ]}
        showBackButton
        backPath={SIMULATION_SETUP_PATH}
        actions={
          <Button
            variant="outlined"
            onClick={() => navigate(SIMULATION_SETUP_PATH)}
            startIcon={<ArrowBackIcon />}
          >
            Back to Patients
          </Button>
        }
      />

      <SessionModeToggle
        value={selectedMode}
        onChange={setSelectedMode}
        modeOptions={sessionModeOptions}
        sx={{ mb: 3 }}
      />

      {/* Guest Mode Notice */}
      {isGuest && (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 3,
            borderRadius: 2
          }}
          icon={<GuestIcon />}
        >
          <Typography variant="body2">
            <strong>Guest Mode:</strong> You're browsing as a guest. Simulations will not be saved to your history.
            <Button 
              size="small" 
              sx={{ ml: 2 }} 
              onClick={() => navigate('/login')}
            >
              Login to Save Progress
            </Button>
          </Typography>
        </Alert>
      )}

      {/* Debug Info (only in development) */}
      {/* Commented out to hide debug info
      {process.env.NODE_ENV === 'development' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Debug: User={user?.email || 'none'}, Guest={isGuest.toString()}, Authenticated={isAuthenticated.toString()}
        </Alert>
      )}
      */}

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 4,
            borderRadius: 2
          }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {personas.length === 0 ? (
        <Box 
          sx={{ 
            textAlign: 'center', 
            py: 8,
            px: 3,
            borderRadius: 2,
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'grey.200'
          }}
        >
          <PersonIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            No Patient Personas Available
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            There are no patient personas available for simulation at this time.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="outlined"
              onClick={() => navigate(SIMULATION_SETUP_PATH)}
              startIcon={<ArrowBackIcon />}
            >
              Back to Patients
            </Button>
            <Button
              variant="contained"
              onClick={fetchPersonasAndSuperDoctors}
              startIcon={<PlayArrowIcon />}
            >
              Refresh List
            </Button>
          </Stack>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {personas.map((persona) => (
            <Grid item xs={12} md={6} lg={4} key={persona.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.2s ease-in-out',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                    borderColor: 'primary.main'
                  }
                }}
              >
                <CardContent sx={{ flex: 1, p: 3 }}>
                  {/* Patient Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <PatientProfileAvatar persona={persona} size={56} sx={{ mr: 2 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        {persona.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {persona.age} years old • {persona.gender}
                      </Typography>
                    </Box>
                    {persona.difficulty && (
                      <Chip
                        label={persona.difficulty}
                        size="small"
                        color={getDifficultyColor(persona.difficulty)}
                        sx={{ textTransform: 'capitalize' }}
                      />
                    )}
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  {/* Chief Complaint */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <MedicalIcon sx={{ fontSize: 20, mr: 1, color: 'error.main' }} />
                      <Typography variant="subtitle2" fontWeight={600} color="error.main">
                        Chief Complaint
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {persona.chief_complaint}
                    </Typography>
                  </Box>

                  {/* Social Background */}
                  {persona.social_background && (
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <InfoIcon sx={{ fontSize: 20, mr: 1, color: 'secondary.main' }} />
                        <Typography variant="subtitle2" fontWeight={600} color="secondary.main">
                          Social Background
                        </Typography>
                      </Box>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{
                          lineHeight: 1.6,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {persona.social_background}
                      </Typography>
                    </Box>
                  )}

                  {/* Case Background */}
                  {persona.vignette && (
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <InfoIcon sx={{ fontSize: 20, mr: 1, color: 'info.main' }} />
                        <Typography variant="subtitle2" fontWeight={600} color="info.main">
                          Case Background
                        </Typography>
                      </Box>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{
                          lineHeight: 1.6,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {persona.vignette}
                      </Typography>
                    </Box>
                  )}

                  {/* Personality Traits */}
                  {persona.personality_traits && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <PsychologyIcon sx={{ fontSize: 20, mr: 1, color: 'secondary.main' }} />
                        <Typography variant="subtitle2" fontWeight={600} color="secondary.main">
                          Personality Traits
                        </Typography>
                      </Box>
                      <Grid container spacing={0.5}>
                        {getPersonalityTraits(persona.personality_traits).map((trait, index) => (
                          <Grid item xs={6} sm={4} key={index}>
                            <Chip
                              label={trait.trim()}
                              size="small"
                              variant="outlined"
                              sx={{ 
                                fontSize: '0.7rem',
                                height: 22,
                                width: '100%',
                                '& .MuiChip-label': {
                                  px: 1
                                }
                              }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </CardContent>

                {/* Action Button */}
                <Box sx={{ p: 3, pt: 0 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={() => handleSelectPersona(persona)}
                    startIcon={<PlayArrowIcon />}
                    sx={{
                      py: 1.5,
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '1rem',
                      borderRadius: 2
                    }}
                  >
                    Start Simulation
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Confirmation Dialog */}
      <Dialog 
        open={showConfirmDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Typography variant="h5" fontWeight={600}>
            Start Simulation with {selectedPersona?.name}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          <Typography gutterBottom color="text.secondary" sx={{ mb: 3 }}>
            You are about to start a medical interview simulation with the following patient:
          </Typography>

          {selectedPersona && (
            <Box 
              sx={{ 
                p: 3, 
                bgcolor: 'grey.50', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                mb: 3
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PatientProfileAvatar persona={selectedPersona} size={48} sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    {selectedPersona.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedPersona.age} years old • {selectedPersona.gender}
                  </Typography>
                </Box>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="body2" gutterBottom>
                <Box component="span" fontWeight={600}>Chief Complaint:</Box> {selectedPersona.chief_complaint}
              </Typography>
              
              {selectedPersona.vignette && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <Box component="span" fontWeight={600}>Background:</Box> {selectedPersona.vignette}
                </Typography>
              )}
            </Box>
          )}

          {/* Super Doctor Selection */}
          {selectedMode !== 'nurse' && superDoctors && superDoctors.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SuperDoctorIcon color="primary" />
                Super Doctor Consultation (Optional)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select a Super Doctor for expert medical consultation during the simulation. You can address them with "Super Doctor" followed by your question.
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Card 
                    sx={{ 
                      p: 2, 
                      cursor: 'pointer',
                      border: selectedSuperDoctor === null ? '2px solid' : '1px solid',
                      borderColor: selectedSuperDoctor === null ? 'primary.main' : 'grey.300',
                      bgcolor: selectedSuperDoctor === null ? 'primary.50' : 'background.paper'
                    }}
                    onClick={() => setSelectedSuperDoctor(null)}
                  >
                    <Typography variant="body2" fontWeight={selectedSuperDoctor === null ? 600 : 400}>
                      No Super Doctor
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Proceed without expert consultation
                    </Typography>
                  </Card>
                </Grid>
                
                {superDoctors.map((superDoctor) => (
                  <Grid item xs={12} sm={6} key={superDoctor.id}>
                    <Card 
                      sx={{ 
                        p: 2, 
                        cursor: 'pointer',
                        border: selectedSuperDoctor?.id === superDoctor.id ? '2px solid' : '1px solid',
                        borderColor: selectedSuperDoctor?.id === superDoctor.id ? 'primary.main' : 'grey.300',
                        bgcolor: selectedSuperDoctor?.id === superDoctor.id ? 'primary.50' : 'background.paper'
                      }}
                      onClick={() => setSelectedSuperDoctor(superDoctor)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32, mr: 1 }}>
                          <SuperDoctorIcon fontSize="small" />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={selectedSuperDoctor?.id === superDoctor.id ? 600 : 400}>
                            {superDoctor.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Attending Physician
                          </Typography>
                        </Box>
                      </Box>
                      {superDoctor.character && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {superDoctor.character.length > 80 
                            ? `${superDoctor.character.substring(0, 80)}...` 
                            : superDoctor.character}
                        </Typography>
                      )}
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              This simulation will help you practice your clinical interview skills. 
              Take your time to ask thoughtful questions and gather comprehensive patient information.
            </Typography>
          </Alert>

          {/* Guest mode warning */}
          {isGuest && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Guest Mode:</strong> This simulation will not be saved to your history. 
                Consider creating an account to track your progress.
              </Typography>
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={handleCloseDialog}
            disabled={startingSimulation}
            size="large"
          >
            Cancel
          </Button>
          <Button
            onClick={handleStartSimulation}
            variant="contained"
            disabled={startingSimulation}
            startIcon={startingSimulation ? <CircularProgress size={20} /> : <PlayArrowIcon />}
            size="large"
            sx={{ ml: 2 }}
          >
            {startingSimulation ? 'Starting...' : 'Start Simulation'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
};

export default PersonaList;
